<div class="cd-page">
  <app-counselor-nav></app-counselor-nav>

  <main class="cd-main" aria-label="Assign students to faculty">
    <!-- Header toolbar -->
    <mat-toolbar class="cd-header-toolbar">
      <div class="cd-header-left">
        <h1 class="cd-title">Assign Students</h1>
        <span class="cd-subtitle"> Connect Students and Faculty </span>
      </div>
      <span class="cd-toolbar-spacer" aria-hidden="true"></span>
      <mat-form-field appearance="outline" class="ms-search-field">
        <mat-label>Search students</mat-label>
        <mat-icon matPrefix aria-hidden="true">search</mat-icon>
        <input
          matInput
          [ngModel]="searchTerm()"
          (ngModelChange)="setSearchTerm($event)"
          placeholder="e.g. Ava Thompson"
          aria-label="Search students by name"
          autocomplete="off"
        />
        @if (searchTerm()) {
          <button matSuffix mat-icon-button (click)="setSearchTerm('')" aria-label="Clear search">
            <mat-icon aria-hidden="true">close</mat-icon>
          </button>
        }
      </mat-form-field>
    </mat-toolbar>

    <!-- Filter + summary toolbar -->
    <mat-toolbar class="ms-filters-toolbar" aria-label="Assignment filters">
      <mat-icon class="ms-filter-toolbar-icon" aria-hidden="true">filter_list</mat-icon>
      <span class="ms-filter-label" aria-hidden="true">Status</span>
      <mat-chip-listbox aria-label="Filter by status" class="ms-chip-listbox">
        <mat-chip-option [selected]="statusFilter() === 'all'" (click)="setStatusFilter('all')">
          All
        </mat-chip-option>
        <mat-chip-option
          [selected]="statusFilter() === 'missing'"
          (click)="setStatusFilter('missing')"
        >
          No faculty connected
          @if (missingCount() > 0) {
            <span class="as-chip-count">{{ missingCount() }}</span>
          }
        </mat-chip-option>
      </mat-chip-listbox>

      <span class="cd-toolbar-spacer" aria-hidden="true"></span>

      <div class="as-stats" aria-label="Assignment summary">
        <span class="as-stat">
          <span class="as-stat-value">{{ assignedCount() }}</span>
          <span class="as-stat-label">Assigned</span>
        </span>
        <mat-divider vertical class="ms-toolbar-divider"></mat-divider>
        <span class="as-stat as-stat-warn">
          <span class="as-stat-value">{{ missingCount() }}</span>
          <span class="as-stat-label">Missing</span>
        </span>
      </div>

      <button
        mat-flat-button
        *hasPermission="permissions.ASSIGNMENTS.BULK_UPLOAD"
        class="as-upload-btn"
        (click)="openCsvUpload()"
      >
        <mat-icon aria-hidden="true">upload_file</mat-icon>
        Upload CSV
      </button>
    </mat-toolbar>

    @if (selectedCount() > 0) {
      <mat-toolbar
        class="ms-filters-toolbar as-bulk-toolbar"
        aria-label="Bulk assign selected students"
      >
        <mat-icon class="ms-filter-toolbar-icon" aria-hidden="true">playlist_add_check</mat-icon>
        <span class="as-bulk-count">{{ selectedCount() }} selected</span>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="as-bulk-select">
          <mat-select
            [ngModel]="bulkFacultyId()"
            (ngModelChange)="bulkFacultyId.set($event)"
            aria-label="Add faculty to selected students"
          >
            <mat-option value="" disabled>Add faculty…</mat-option>
            @for (faculty of facultyMembers(); track faculty.id) {
              <mat-option [value]="faculty.id.toString()">{{ displayName(faculty) }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <button
          mat-flat-button
          color="primary"
          [disabled]="!bulkFacultyId()"
          (click)="applyBulkAssign()"
        >
          Add
        </button>
        <button mat-button (click)="clearSelection()">Clear</button>

        <span class="cd-toolbar-spacer" aria-hidden="true"></span>
      </mat-toolbar>
    }

    <mat-card class="ms-note ms-note--blue as-roster-card">
      <mat-card-header class="ms-note-header">
        <mat-icon
          mat-card-avatar
          aria-hidden="true"
          class="ms-note-avatar-icon ms-note-avatar-icon--blue"
        >
          groups
        </mat-icon>
        <mat-card-title>Roster</mat-card-title>
        <mat-card-subtitle>{{ filteredStudents().length }} shown</mat-card-subtitle>
      </mat-card-header>
      <mat-divider></mat-divider>

      <mat-card-content class="ms-note-body as-table-body">
        <table
          mat-table
          [dataSource]="filteredStudents()"
          class="roster-table"
          multiTemplateDataRows
        >
          <ng-container matColumnDef="select">
            <th mat-header-cell *matHeaderCellDef class="col-check">
              <mat-checkbox
                [checked]="allVisibleSelected()"
                [indeterminate]="someVisibleSelected()"
                (change)="toggleSelectAllVisible()"
                [aria-label]="'Select all visible students'"
              ></mat-checkbox>
            </th>
            <td mat-cell *matCellDef="let student" class="col-check">
              <mat-checkbox
                [checked]="isSelected(student.id)"
                (change)="toggleSelected(student.id)"
                [aria-label]="'Select ' + student.name"
              ></mat-checkbox>
            </td>
          </ng-container>

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Student</th>
            <td mat-cell *matCellDef="let student" class="col-name">{{ student.name }}</td>
          </ng-container>

          <ng-container matColumnDef="grade">
            <th mat-header-cell *matHeaderCellDef>Grade</th>
            <td mat-cell *matCellDef="let student">{{ student.grade }}</td>
          </ng-container>

          <ng-container matColumnDef="faculty">
            <th mat-header-cell *matHeaderCellDef>Faculty</th>
            <td mat-cell *matCellDef="let student" class="col-faculty">
              <button
                type="button"
                class="faculty-summary"
                (click)="toggleExpand(student.id)"
                [attr.aria-expanded]="isExpanded(student.id)"
                [attr.aria-label]="'Toggle faculty connections for ' + student.name"
              >
                @if (student.connections.length > 0) {
                  <span class="faculty-avatar-stack" aria-hidden="true">
                    @for (conn of visibleConnections(student); track conn.faculty.id) {
                      <span class="faculty-avatar">
                        <app-user-avatar
                          [imageUrl]="conn.faculty.Profile?.profile_picture_path"
                          [name]="displayName(conn.faculty)"
                        />
                      </span>
                    }
                    @if (overflowCount(student) > 0) {
                      <span class="faculty-avatar faculty-avatar-overflow"
                        >+{{ overflowCount(student) }}</span
                      >
                    }
                  </span>
                  <span class="faculty-summary-count">{{ student.connections.length }}</span>
                } @else {
                  <span class="faculty-summary-empty">None</span>
                }
                <mat-icon class="faculty-summary-caret" aria-hidden="true">{{
                  isExpanded(student.id) ? 'expand_less' : 'expand_more'
                }}</mat-icon>
              </button>
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let student">
              <mat-chip-set aria-hidden="true">
                @if (student.connections.length > 0) {
                  <mat-chip disableRipple class="ms-status-chip chip-assigned">Assigned</mat-chip>
                } @else {
                  <mat-chip
                    disableRipple
                    class="ms-status-chip chip-missing"
                    matTooltip="Flag this student for follow-up"
                  >
                    Missing
                  </mat-chip>
                }
              </mat-chip-set>
            </td>
          </ng-container>

          <!-- Expanded detail row: full connection cards + add control -->
          <ng-container matColumnDef="expandedDetail">
            <td
              mat-cell
              *matCellDef="let student"
              [attr.colspan]="displayedColumns.length"
              class="detail-cell"
            >
              <div class="student-detail" [class.is-expanded]="isExpanded(student.id)">
                <div class="student-detail-inner">
                  @if (student.connections.length === 0) {
                    <div class="detail-empty">No faculty connected yet</div>
                  }

                  <div class="detail-cards">
                    @for (conn of student.connections; track conn.connectionId) {
                      <div class="detail-card">
                        <span class="faculty-avatar" aria-hidden="true">
                          <app-user-avatar
                            [imageUrl]="conn.faculty.Profile?.profile_picture_path"
                            [name]="displayName(conn.faculty)"
                          />
                        </span>

                        <span class="detail-card-name">
                          {{ displayName(conn.faculty) }}
                        </span>

                        <button
                          mat-icon-button
                          class="detail-card-lock"
                          [class.is-locked]="conn.locked"
                          (click)="toggleLock(student.id, conn.faculty.id)"
                          [matTooltip]="
                            conn.locked
                              ? 'Locked - student can\'t be remove'
                              : 'Click to lock this connection'
                          "
                          [attr.aria-label]="
                            (conn.locked ? 'Unlock' : 'Lock') +
                            ' connection with ' +
                            displayName(conn.faculty)
                          "
                        >
                          <mat-icon aria-hidden="true">
                            {{ conn.locked ? 'lock' : 'lock_open' }}
                          </mat-icon>
                        </button>

                        <button
                          mat-icon-button
                          class="detail-card-remove"
                          [disabled]="conn.locked"
                          (click)="removeConnection(student.id, conn.faculty.id)"
                          [attr.aria-label]="'Remove connection with ' + displayName(conn.faculty)"
                        >
                          <mat-icon aria-hidden="true">close</mat-icon>
                        </button>
                      </div>
                    }
                  </div>

                  <div class="detail-add">
                    <mat-form-field
                      appearance="outline"
                      subscriptSizing="dynamic"
                      class="faculty-add-field"
                    >
                      <mat-select
                        [ngModel]="pendingFacultyIdFor(student.id)"
                        (ngModelChange)="setPendingFaculty(student.id, $event)"
                        placeholder="Add faculty…"
                        [aria-label]="'Add faculty connection for ' + student.name"
                      >
                        @for (faculty of availableFacultyOptions(student); track faculty.id) {
                          <mat-option [value]="faculty.id.toString()">{{
                            displayName(faculty)
                          }}</mat-option>
                        } @empty {
                          <mat-option disabled>All faculty already connected</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                    <button
                      mat-flat-button
                      color="primary"
                      [disabled]="!pendingFacultyIdFor(student.id)"
                      (click)="addConnection(student.id, pendingFacultyIdFor(student.id))"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let student; columns: displayedColumns"
            [class.row-missing]="student.connections.length === 0"
            [class.row-expanded]="isExpanded(student.id)"
          ></tr>
          <tr mat-row *matRowDef="let student; columns: detailColumns" class="detail-row"></tr>

          <tr class="mat-row empty-row" *matNoDataRow>
            <td class="empty-state" [attr.colspan]="displayedColumns.length">
              No students match your filters.
            </td>
          </tr>
        </table>
      </mat-card-content>

      <mat-divider></mat-divider>
      <mat-card-footer class="ms-note-footer">
        <mat-icon aria-hidden="true">info_outline</mat-icon>
        <span aria-live="polite">
          {{ filteredStudents().length }} student{{ filteredStudents().length !== 1 ? 's' : '' }}
          shown
        </span>
      </mat-card-footer>
    </mat-card>
  </main>
</div>
